// ================================================================
//  CreateBizEventWizardController
// ================================================================

angular.module('bizmetryApp').controller('CreateBizEventWizardController', function (
    $scope,
    $rootScope,
    $q,
    $mdDialog,
    $mdToast,
    $timeout,
    $interval,
    ApiCollectionService,
    TemplateInstanceService,
    BusinessEventsService,
    ProfileService,
    BmelEditorService,
    dialogService,
    profile
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    // ========================================
    // WIZARD STATE — 6 steps
    // ========================================

    ctrl.steps = [
        { label: 'Event Source' },
        { label: 'Target Frame' },
        { label: 'Select Operations' },
        { label: 'Configure Operations' },
        { label: 'Name & Description' },
        { label: 'Configure Mapping' }
    ];

    ctrl.totalSteps = ctrl.steps.length;
    ctrl.currentStep = 1;

    ctrl.stepLabel = function () { return ctrl.steps[ctrl.currentStep - 1].label; };

    // ========================================
    // STEP 5 — EVENT IDENTITY (name/description)
    // ========================================
    // Name and description are systematically generated by BizMetry once
    // the target frame type and operations are known — they're what
    // actually identify the biz event: (a) profile name, (b) target frame
    // type name + version, (c) a deterministic hash of the selected
    // operations, plus (d) a short time-based serializer for uniqueness.
    // The user can still edit either field afterwards; once they do, we
    // stop overwriting it on subsequent auto-generation passes (e.g. if
    // they go back and change the target/operations and come forward
    // again) unless they explicitly hit "Regenerate".

    ctrl.bizEventName        = '';
    ctrl.existingEventNames  = [];   // lowercased names loaded on init
    ctrl.nameAlreadyExists   = false;
    ctrl.loadingNames        = true;
    ctrl.bizEventDescription = '';
    ctrl._identityTouched    = false;   // true once the user manually edits name/description
    ctrl._identityGenerated  = false;   // true once auto-generated at least once

    // Load all existing event names for this profile upfront (client-side validation)
    BusinessEventsService.getAllNames(profile.id)
        .then(function (names) { ctrl.existingEventNames = names; })
        .catch(function ()     { ctrl.existingEventNames = []; })
        .finally(function ()   { ctrl.loadingNames = false; });

    // Called on every keystroke in the name field
    ctrl.onNameChange = function () {
        var trimmed = (ctrl.bizEventName || '').trim().toLowerCase();
        ctrl.nameAlreadyExists = trimmed.length > 0
            && ctrl.existingEventNames.indexOf(trimmed) !== -1;
    };

    // Marks the identity as manually edited so auto-generation won't
    // clobber it if the user revisits this step later.
    ctrl.onIdentityFieldEdited = function () {
        ctrl._identityTouched = true;
        ctrl.onNameChange();
    };

    function _slug(str, maxLen) {
        return (str || '')
            .toString()
            .trim()
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, maxLen || 30);
    }

    // Deterministic djb2-style string hash, base36-encoded for compactness.
    function _djb2Base36(str) {
        var s = str || '', hash = 5381;
        for (var i = 0; i < s.length; i++) { hash = ((hash << 5) + hash) + s.charCodeAt(i); hash |= 0; }
        return Math.abs(hash).toString(36);
    }

    // Reproducible signature of the current operation selection — same
    // set of operations (regardless of selection order) always hashes
    // to the same value. Two events can legitimately target the same
    // profile, frame type/version and operations while still mapping
    // those operations to different frame components, so the ops hash
    // alone isn't enough to keep names unique — see _timeSerial below.
    function _operationsSignature() {
        var keys = (ctrl.selectedOperations || []).map(function (s) {
            return (s.collection.collection_id || '') + '|' +
                (s.op.operationId || (s.op._method + ':' + s.op._pathKey));
        });
        keys.sort();
        return keys.join(',');
    }

    // Short time-based serializer — guarantees uniqueness even when the
    // same profile/target/operations combination is used to create more
    // than one biz event (e.g. with different mappings).
    function _timeSerial() {
        return Date.now().toString(36);
    }

    // Auto-generates name/description from the current selections — but
    // only the FIRST time this step is reached. No-op on subsequent
    // visits (whether the user touched the fields or not) so that simply
    // going back-and-forth between steps doesn't keep changing the name
    // out from under them via a fresh timestamp. Explicit regeneration
    // is still available via ctrl.regenerateIdentity() (the UI button).
    ctrl._generateEventIdentity = function () {
        if (ctrl._identityGenerated || ctrl._identityTouched) { return; }
        ctrl.regenerateIdentity();
    };

    ctrl.regenerateIdentity = function () {
        var profileSlug = _slug(profile.profileName || profile.name);
        var frameSlug    = ctrl.selectedFrameType ? _slug(ctrl.frameTypeName(ctrl.selectedFrameType)) : 'frame';
        var version      = (ctrl.selectedTemplate && ctrl.selectedTemplate.version) || '0';
        var opsHash      = _djb2Base36(_operationsSignature());
        var timeSerial   = _timeSerial();

        ctrl.bizEventName = [profileSlug, frameSlug, 'v' + version, opsHash, timeSerial]
            .filter(function (part) { return !!part; })
            .join('-')
            .substring(0, 120);

        var opsCount = (ctrl.selectedOperations || []).length;
        var collectionNames = [];
        (ctrl.selectedOperations || []).forEach(function (s) {
            var n = s.collection && s.collection.collection_name;
            if (n && collectionNames.indexOf(n) === -1) { collectionNames.push(n); }
        });

        ctrl.bizEventDescription = (
            'Auto-generated for profile "' + (profile.profileName || profile.name || '') +
            '", targeting frame type "' + (ctrl.selectedFrameType ? ctrl.frameTypeName(ctrl.selectedFrameType) : '') +
            '" v' + version + ', based on ' + opsCount + ' operation' + (opsCount === 1 ? '' : 's') +
            (collectionNames.length ? ' from ' + collectionNames.join(', ') : '') +
            '. Generated on ' + new Date().toLocaleString() + '.'
        ).substring(0, 500);

        ctrl._identityTouched   = false;
        ctrl._identityGenerated = true;
        ctrl.onNameChange();
    };

    // ========================================
    // STEP 1 — SOURCE GRAMMAR (reference data)
    // ========================================

    ctrl.sourceTypes = [];
    ctrl.selectedSourceType = null;
    ctrl.loadingSourceTypes = true;

    BusinessEventsService.getGrammarTypes()
        .then(function (types) {
            ctrl.sourceTypes = types;
            ctrl.selectedSourceType = types.find(function (t) { return t.enabled; }) || null;
        })
        .catch(function (error) {
            console.error('[CreateBizEventWizard] Error loading grammars:', error);
        })
        .finally(function () {
            ctrl.loadingSourceTypes = false;
        });

    ctrl.selectSourceType = function (sourceType) {
        if (!sourceType.enabled) { return; }
        ctrl.selectedSourceType = sourceType;
    };

    // ========================================
    // STEP 3 — SELECT OPERATIONS
    // ========================================

    ctrl.loadingCollections = false;
    ctrl.browsingCollection  = null;   // collection currently shown in ops panel
    ctrl.scopedCollections   = [];   // [{collection, operations}] — in scope for IntelliSense
    ctrl.operations          = [];
    ctrl.displayedOperations = [];
    ctrl.loadingOperations   = false;
    ctrl.operationSearch     = '';
    ctrl.selectedOperations  = [];
    ctrl.intelliSenseSelecting  = false;
    ctrl.intelliSenseSelectionError = null;
    ctrl._intelliSenseMappings  = null;   // raw API mappings — passed to mapping step
    // Set when the user picked operations with IntelliSense: the agent then builds
    // the mappings once, when leaving Configure Operations (see ctrl.nextStep).
    ctrl._intelliSenseUsed      = false;
    // Signature of the source/target the agent last mapped (target frame +
    // operations + cardinality): leaving Configure Operations with a different
    // one runs the agent again (see _agentMappingNeeded).
    ctrl._agentInputs           = null;

    // Called by biz-event-mapping-step when the user clicks "Clear All".
    // preloadMappings is bound one-way ('<') into the step, so clearing
    // ctrl.mappings there never reaches _intelliSenseMappings here. If we
    // don't null it out, navigating Back → Next re-creates the directive,
    // its preload watch fires again with this same (still populated) data,
    // and the mappings the user just cleared silently come back.
    ctrl.discardIntelliSensePreload = function () {
        ctrl._intelliSenseMappings = null;
        ctrl._intelliSenseUsed     = false;   // and don't rebuild them on the next pass through step 4
    };
    ctrl._pendingScopeCollectionId = null;  // set when scope-on-load is requested
    ctrl.collectionSearch          = '';

    // The API collections the selected template version exposes (template
    // editor → API Collections), one version per collection. Biz events consume
    // APIs through their template, so these are the only ones offered.
    ctrl.templateApiCollections     = [];
    ctrl.loadingTemplateCollections = false;

    $scope.$watch(function () { return ctrl.selectedTemplate && ctrl.selectedTemplate.id; }, function (templateId) {
        ctrl.templateApiCollections = [];
        if (!templateId) { return; }
        ctrl.loadingTemplateCollections = true;
        TemplateInstanceService.getApiCollections(templateId, { skipLoader: true })
            .then(function (res) {
                if (!ctrl.selectedTemplate || ctrl.selectedTemplate.id !== templateId) { return; }
                ctrl.templateApiCollections = (res.data && res.data.api_collections) || [];
                _alignSelectionToTemplate(ctrl.templateApiCollections);
            })
            .catch(function () { ctrl.templateApiCollections = []; })
            .finally(function () { ctrl.loadingTemplateCollections = false; });
    });

    // A biz event only uses the collection versions linked to its template. When
    // the user goes back and picks another template, each selected operation of a
    // collection that template doesn't link moves to the linked version of the same
    // collection (same operation, kept at its index so its mappings survive) or,
    // when there's no such operation, is dropped along with its mappings.
    function _alignSelectionToTemplate(linked) {
        var linkedIds = {}, byName = {};
        linked.forEach(function (c) { linkedIds[c.collection_id] = true; byName[c.collection_name] = c; });

        ctrl.scopedCollections = ctrl.scopedCollections.filter(function (s) {
            return linkedIds[s.collection.collection_id];
        });
        if (ctrl.browsingCollection && !linkedIds[ctrl.browsingCollection.collection_id]) {
            ctrl.browsingCollection  = null;
            ctrl.operations          = [];
            ctrl.displayedOperations = [];
        }

        var stale = ctrl.selectedOperations.filter(function (s) { return !linkedIds[s.collection.collection_id]; });
        if (!stale.length) { return; }

        var targetIds = {};
        stale.forEach(function (s) {
            var t = byName[s.collection.collection_name];
            if (t) { targetIds[t.collection_id] = true; }
        });
        $q.all(Object.keys(targetIds).map(function (id) {
            return ApiCollectionService.getById(id)
                .then(function (r) { return r && r.data ? r.data : r; })
                .catch(function () { return null; });
        })).then(function (fulls) {
            var opsByCollection = {};
            fulls.forEach(function (full) {
                if (!full) { return; }
                opsByCollection[full.collection_id] = {
                    collection: full,
                    ops: full.operations ? ctrl._opsFromMetadata(full.operations) : ctrl._parseOperations(full.swagger)
                };
            });

            var moved = [], dropped = [], toRemove = [];
            stale.forEach(function (entry) {
                var t = byName[entry.collection.collection_name];
                var target = t && opsByCollection[t.collection_id];
                var match = target && (target.ops.find(function (op) {
                    return op.operationId && op.operationId === entry.op.operationId;
                }) || target.ops.find(function (op) {
                    return op._method === entry.op._method && op._pathKey === entry.op._pathKey;
                }));
                if (!match) {
                    dropped.push(_opLabel(entry.op));
                    toRemove.push(entry);
                    return;
                }
                // Already selected from the new version too: keep this entry (it holds the mappings).
                ctrl.selectedOperations.forEach(function (other) {
                    if (other !== entry && other.collection.collection_id === target.collection.collection_id &&
                        other.op._method === match._method && other.op._pathKey === match._pathKey) {
                        toRemove.push(other);
                    }
                });
                var optionsByType = {};
                ctrl.correlationSourceTypes.forEach(function (cst) {
                    var opts = _computeCorrelationOptions(match, cst.id);
                    opts.sort(function (a, b) { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0; });
                    optionsByType[cst.id] = opts;
                });
                entry.op = match;
                entry.collection = target.collection;
                entry._optionsByType = optionsByType;
                moved.push(target.collection);
            });

            toRemove
                .map(function (e) { return { entry: e, idx: ctrl.selectedOperations.indexOf(e) }; })
                .filter(function (r) { return r.idx !== -1; })
                .sort(function (a, b) { return b.idx - a.idx; })
                .forEach(function (r) { _doRemoveSelectedOperation(r.entry, r.idx); });

            ctrl._intelliSenseMappings = null;

            var lines = [];
            if (moved.length) {
                var versions = {};
                moved.forEach(function (c) { versions[c.collection_name + ' v' + c.api_version] = true; });
                lines.push('Operations moved to ' + Object.keys(versions).join(', '));
            }
            if (dropped.length) {
                lines.push('Removed (not in this template\'s API collections): ' + dropped.join(', '));
            }
            if (lines.length) {
                $mdToast.show($mdToast.simple().textContent(lines.join('. ')).position('top right').hideDelay(6000));
            }
        });
    }

    function _opLabel(op) {
        return (op && (op.operationName || op.summary || op.path)) || 'operation';
    }

    // Matches name, description or version.
    ctrl.filteredCollections = function () {
        var term = (ctrl.collectionSearch || '').trim().toLowerCase();
        if (!term) { return ctrl.templateApiCollections; }
        return ctrl.templateApiCollections.filter(function (c) {
            return (c.collection_name || '').toLowerCase().indexOf(term) !== -1 ||
                   (c.description || '').toLowerCase().indexOf(term) !== -1 ||
                   (c.api_version || '').toLowerCase().indexOf(term.replace(/^v/, '')) !== -1;
        });
    };

    // ========================================
    // STEP 4 — CONFIGURE OPERATIONS
    // ========================================

    ctrl.interceptionModes = [
        { id: 'INBOUND', label: 'Inbound', icon: 'call_received', description: 'Capture events arriving into your system.' },
        { id: 'OUTBOUND', label: 'Outbound', icon: 'call_made', description: 'Capture events sent to external destinations.' },
        { id: 'INBOUND_OUTBOUND', label: 'Inbound & Outbound', icon: 'swap_horiz', description: 'Capture events in both directions.' }
    ];

    // Expected invocations of the operation per business transaction.
    // MANY turns the whole operation into an ARRAY of N occurrences in the
    // mapping step ({Op:Section[]}.$.path refs, NESTED_TABLE one row/invocation).
    ctrl.cardinalities = [
        { id: 'ONE',  label: 'One',  hint: '1:1 — a single invocation per biz event' },
        { id: 'MANY', label: 'Many', hint: '1:N — N invocations per biz event, captured as an array' }
    ];

    // The agent's mappings depend on each operation's cardinality (MANY →
    // {Op:Section[]} refs, aggregations, per-invocation tables). Changing it
    // invalidates them — same rule as adding/removing an operation — so the
    // agent runs again when leaving this step (if nothing is mapped yet).
    ctrl.onCardinalityChange = function () {
        ctrl._intelliSenseMappings = null;
    };

    ctrl.correlationSourceTypes = [
        { id: 'HEADER', label: 'HTTP Header', icon: 'http' },
        { id: 'CUSTOM_HEADER', label: 'Custom Header', icon: 'edit' },
        { id: 'URI_PARAM', label: 'URI Parameter', icon: 'link' },
        { id: 'QUERY_PARAM', label: 'Query Parameter', icon: 'tune' },
        { id: 'REQUEST_FIELD', label: 'Request Field', icon: 'arrow_upward' },
        { id: 'RESPONSE_FIELD', label: 'Response Field', icon: 'arrow_downward' }
    ];

    ctrl.httpStatusDescription = require('../../../../common/HttpStatusDescriptions.js');
    ctrl.httpStatusCodes = Array.from({ length: 500 }, function (_, i) { return String(100 + i); });
    ctrl.filterSourceTypes = [
        { id: 'STATUS_CODE', label: 'HTTP Status Code', icon: 'http' },
        { id: 'HEADER', label: 'HTTP Header', icon: 'http' },
        { id: 'CUSTOM_HEADER', label: 'Custom Header', icon: 'edit' },
        { id: 'URI_PARAM', label: 'URI Parameter', icon: 'link' },
        { id: 'QUERY_PARAM', label: 'Query Parameter', icon: 'tune' },
        { id: 'REQUEST_FIELD', label: 'Request Field', icon: 'arrow_upward' },
        { id: 'RESPONSE_FIELD', label: 'Response Field', icon: 'arrow_downward' }
    ];

    // ----------------------------------------
    // LOAD PUBLISHED COLLECTIONS
    // ----------------------------------------

    // Biz events can be built on templates / collections still in progress
    // (DRAFT, PENDING_REVIEW, …) — only retired ones are off-limits. Publishing
    // the biz event itself still requires both to be PUBLISHED (enforced server-side).
    // `publishedTemplates` keeps its name but holds every selectable template version.
    function _isSelectableStatus(status) {
        return status !== 'RETIRED' && status !== 'DECOMMISSIONED';
    }


    // ----------------------------------------
    // SELECT COLLECTION → LOAD OPERATIONS
    // ----------------------------------------

    ctrl.selectCollection = function (collection) {
        // Switch the browsing panel (always allowed, even if already scoped)
        ctrl.browsingCollection = collection;
        ctrl.operations = [];
        ctrl.displayedOperations = [];
        ctrl.operationSearch = '';

        var loadOps = function (col) {
            var ops = col.operations
                ? ctrl._opsFromMetadata(col.operations)
                : ctrl._parseOperations(col.swagger);
            ctrl.operations = ops;
            ctrl.displayedOperations = ops;
            ctrl.loadingOperations = false;

            // If already scoped, refresh its operations cache with the freshly-loaded data
            var entry = ctrl.scopedCollections.filter(function (s) {
                return s.collection.collection_id === col.collection_id;
            })[0];
            if (entry) {
                entry.collection = col;
                entry.operations = ops;
            }

            // If a scope-on-load was requested for this collection, add it now
            if (ctrl._pendingScopeCollectionId === col.collection_id) {
                ctrl._pendingScopeCollectionId = null;
                if (!entry) {
                    ctrl.scopedCollections.push({ collection: col, operations: ops });
                }
            }
        };

        if (!collection.swagger && !collection.operations) {
            ctrl.loadingOperations = true;
            ApiCollectionService.getById(collection.collection_id)
                .then(function (response) {
                    ctrl.browsingCollection = response.data || response;
                    loadOps(ctrl.browsingCollection);
                })
                .catch(function (err) {
                    console.error('[BizEventWizard] Error fetching collection:', err);
                    ctrl.loadingOperations = false;
                });
        } else {
            ctrl.loadingOperations = true;
            $timeout(function () { loadOps(collection); }, 0);
        }
    };

    ctrl.isBrowsingCollection = function (col) {
        return ctrl.browsingCollection &&
               ctrl.browsingCollection.collection_id === col.collection_id;
    };

    ctrl.isScopedCollection = function (col) {
        return ctrl.scopedCollections.some(function (s) {
            return s.collection.collection_id === col.collection_id;
        });
    };

    ctrl.removeScopedCollection = function (col, $event) {
        if ($event) { $event.stopPropagation(); }
        // Remove from scope
        ctrl.scopedCollections = ctrl.scopedCollections.filter(function (s) {
            return s.collection.collection_id !== col.collection_id;
        });
        // Also remove any selected operations that came from this collection
        ctrl.selectedOperations = ctrl.selectedOperations.filter(function (s) {
            return s.collection.collection_id !== col.collection_id;
        });
        // Clear browsing if we just removed what was being browsed
        if (ctrl.browsingCollection &&
            ctrl.browsingCollection.collection_id === col.collection_id) {
            ctrl.browsingCollection = null;
            ctrl.operations = [];
            ctrl.displayedOperations = [];
        }
        ctrl._intelliSenseMappings = null;
    };

    ctrl.toggleScopedCollection = function (col, $event) {
        if ($event) { $event.stopPropagation(); }

        if (ctrl.isScopedCollection(col)) {
            ctrl.removeScopedCollection(col, $event);
            return;
        }

        ctrl._intelliSenseMappings = null;


        // If already browsing this collection with ops loaded → scope immediately
        var alreadyBrowsed = ctrl.browsingCollection &&
                             ctrl.browsingCollection.collection_id === col.collection_id &&
                             !ctrl.loadingOperations;

        if (alreadyBrowsed) {
            ctrl.scopedCollections.push({
                collection: ctrl.browsingCollection,
                operations: ctrl.operations
            });
        } else {
            // Browse first (loads ops), auto-scope once ops are ready
            ctrl._pendingScopeCollectionId = col.collection_id;
            ctrl.selectCollection(col);
        }
    };

    // ----------------------------------------
    // BUILD OPERATIONS FROM PRE-PARSED METADATA
    // ----------------------------------------

    ctrl._opsFromMetadata = function (operations) {
        if (!operations || !operations.length) { return []; }
        return operations.map(function (op, i) {
            return {
                _index: i,
                _pathKey: op.path,
                _method: (op.method || '').toLowerCase(),
                method: (op.method || '').toUpperCase(),
                path: op.path,
                operationId: op.operation_id || null,
                operationName: op.operation_name || null,
                summary: op.summary || null,
                description: op.description || null,
                tags: op.tags || [],
                signature: op.signature || op.path,
                queryParams: op.query_params || [],
                uriParams: op.uri_params || [],
                httpHeaders: op.http_headers || [],
                payload: op.payload || null
            };
        });
    };

    // ----------------------------------------
    // BUILD OPERATIONS FROM RAW SWAGGER
    // ----------------------------------------

    var HTTP_METHODS = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'];
    var METHOD_ORDER = ['delete', 'get', 'head', 'options', 'patch', 'post', 'put'];

    ctrl._parseOperations = function (swagger) {
        if (!swagger || !swagger.paths) { return []; }
        var ops = [], index = 0;
        Object.keys(swagger.paths).forEach(function (path) {
            var pathItem = swagger.paths[path];
            HTTP_METHODS.forEach(function (method) {
                if (!pathItem[method]) { return; }
                var op = pathItem[method];
                ops.push({
                    _index: index++, _pathKey: path, _method: method, method: method.toUpperCase(),
                    path: path, operationId: op.operationId || null, summary: op.summary || null,
                    description: op.description || null, tags: op.tags || [],
                    queryParams: [], uriParams: [], httpHeaders: []
                });
            });
        });
        ops.sort(function (a, b) {
            var mA = METHOD_ORDER.indexOf(a._method), mB = METHOD_ORDER.indexOf(b._method);
            if (mA !== mB) { return mA - mB; }
            var nA = (a.operationId || a.summary || a.path || '').toLowerCase();
            var nB = (b.operationId || b.summary || b.path || '').toLowerCase();
            return nA < nB ? -1 : nA > nB ? 1 : 0;
        });
        return ops;
    };

    // ----------------------------------------
    // OPERATION SEARCH
    // ----------------------------------------

    ctrl._applyOperationSearch = function () {
        if (!ctrl.operationSearch || !ctrl.operationSearch.trim()) {
            ctrl.displayedOperations = ctrl.operations;
            return;
        }
        var term = ctrl.operationSearch.trim().toLowerCase();
        ctrl.displayedOperations = ctrl.operations.filter(function (op) {
            return (op.operationName || '').toLowerCase().indexOf(term) !== -1 ||
                (op.summary || '').toLowerCase().indexOf(term) !== -1 ||
                (op.description || '').toLowerCase().indexOf(term) !== -1 ||
                (op.path || '').toLowerCase().indexOf(term) !== -1;
        });
    };

    $scope.$watch(function () { return ctrl.operationSearch; }, function () { ctrl._applyOperationSearch(); });
    $scope.$watch(function () { return ctrl.operations; }, function () {
        ctrl.operationSearch = '';
        ctrl.displayedOperations = ctrl.operations;
    });

    // ----------------------------------------
    // MULTI-SELECT OPERATIONS
    // ----------------------------------------

    ctrl.isOperationSelected = function (op) {
        if (!ctrl.browsingCollection) { return false; }
        return ctrl.selectedOperations.some(function (s) {
            return s.op._pathKey === op._pathKey &&
                s.op._method === op._method &&
                s.collection.collection_id === ctrl.browsingCollection.collection_id;
        });
    };

    ctrl.toggleOperation = function (op) {
        if (ctrl.isOperationSelected(op)) { ctrl._removeOperation(op, ctrl.browsingCollection); }
        else { ctrl._addOperation(op, ctrl.browsingCollection); }
    };


    ctrl._addOperation = function (op, collection) {
        ctrl._intelliSenseMappings = null;  // manual edit invalidates IS preload
        var optionsByType = {};
        ctrl.correlationSourceTypes.forEach(function (cst) {
            var opts = _computeCorrelationOptions(op, cst.id);
            opts.sort(function (a, b) { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0; });
            optionsByType[cst.id] = opts;
        });

        ctrl.selectedOperations.push({
            op: op,
            collection: collection,
            interceptionMode: 'INBOUND',
            cardinality: 'ONE',
            correlationKey: { sourceType: null, sourcePath: '' },
            filters: [],
            _optionsByType: optionsByType,
            _configOpen: true
        });

        ctrl.selectedOperations.sort(function (a, b) {
            var colA = (a.collection.collection_name || '').toLowerCase();
            var colB = (b.collection.collection_name || '').toLowerCase();
            if (colA !== colB) { return colA < colB ? -1 : 1; }
            var opA = (a.op.operationName || a.op.summary || a.op.path || '').toLowerCase();
            var opB = (b.op.operationName || b.op.summary || b.op.path || '').toLowerCase();
            return opA < opB ? -1 : opA > opB ? 1 : 0;
        });
    };

    ctrl._removeOperation = function (op, collection) {
        // Partial invalidation: remove only the IS mappings that reference this
        // specific operation_id. Remaining mappings stay valid — _translateIntelliSenseMappings
        // already skips sources whose operation_id is absent from selectedOperations.
        var removedOpId = String(op.operationId || op.operation_id || op.id || '');
        _pruneIntelliSenseMappingsForOp(removedOpId);

        ctrl.selectedOperations = ctrl.selectedOperations.filter(function (s) {
            return !(s.op._pathKey === op._pathKey && s.op._method === op._method &&
                s.collection.collection_id === collection.collection_id);
        });
    };

    ctrl.removeSelectedOperation = function (entry) {
        var removedIdx = ctrl.selectedOperations.indexOf(entry);

        // Check if this operation already has field mappings configured
        var hasMappings = ctrl.mappings && ctrl.mappings.some(function (m) {
            return m.source && m.source.opIdx === removedIdx;
        });

        if (hasMappings) {
            var opName = (entry.op && (entry.op.operationName || entry.op.summary || entry.op.path))
                         || 'this operation';
            dialogService.showDecisionDialog(
                'Remove Operation',
                'The operation <strong>' + opName + '</strong> already has field mappings configured. ' +
                'If you proceed, those mappings will be permanently removed.'
            ).then(function (decision) {
                if (decision === 'proceed') {
                    _doRemoveSelectedOperation(entry, removedIdx);
                }
            });
        } else {
            _doRemoveSelectedOperation(entry, removedIdx);
        }
    };

    // ── Internal: performs the actual removal + opIdx re-indexing ─────────
    function _doRemoveSelectedOperation(entry, removedIdx) {
        // Remove from operation list
        ctrl.selectedOperations = ctrl.selectedOperations.filter(function (s) { return s !== entry; });

        // 1. Drop mappings that belong to the removed operation
        ctrl.mappings = ctrl.mappings.filter(function (m) {
            return !(m.source && m.source.opIdx === removedIdx);
        });

        // 2. Re-index sources that were after the removed slot
        ctrl.mappings.forEach(function (m) {
            _reindexSource(m.source, removedIdx);
            if (m.columnMappings) {
                Object.keys(m.columnMappings).forEach(function (col) {
                    var cm = m.columnMappings[col];
                    if (cm && cm.source) { _reindexSource(cm.source, removedIdx); }
                });
            }
        });

        // 3. Prune _intelliSenseMappings for the removed operation (partial invalidation).
        //    We keep IS mappings for the remaining operations so the user doesn't lose
        //    all preloaded suggestions when only one operation is removed.
        var removedOpId = String((entry.op.operationId || entry.op.operation_id || entry.op.id) || '');
        _pruneIntelliSenseMappingsForOp(removedOpId);
    }

    // Helper: decrement opIdx and rebuild the DOM-matching ID for sources
    // that were at a higher index than the removed operation.
    function _reindexSource(source, removedIdx) {
        if (!source || source.opIdx <= removedIdx) { return; }
        source.opIdx--;
        if (source.id) {
            source.id = source.id.replace(/^op\d+_/, 'op' + source.opIdx + '_');
        }
    }

    // Helper: remove all sources referencing a specific operation_id from the
    // raw _intelliSenseMappings array. Component entries that end up with zero
    // sources after pruning are also dropped, so _translateIntelliSenseMappings
    // receives a clean payload. If _intelliSenseMappings becomes empty after
    // pruning it is set to null so the preload watcher short-circuits correctly.
    function _pruneIntelliSenseMappingsForOp(removedOpId) {
        if (!ctrl._intelliSenseMappings || !removedOpId) { return; }
        ctrl._intelliSenseMappings = ctrl._intelliSenseMappings
            .map(function (componentMapping) {
                var filteredSources = (componentMapping.sources || []).filter(function (src) {
                    return String(src.operation_id || '') !== removedOpId;
                });
                return { frame_component_id: componentMapping.frame_component_id,
                         sources: filteredSources };
            })
            .filter(function (componentMapping) {
                return componentMapping.sources.length > 0;
            });
        if (ctrl._intelliSenseMappings.length === 0) {
            ctrl._intelliSenseMappings = null;
        }
    }


    ctrl.clearAllSelectedOperations = function () {
        var hasMappings = ctrl.mappings && ctrl.mappings.length > 0;

        function _doClearAll() {
            ctrl.selectedOperations    = [];
            ctrl._intelliSenseMappings = null;
            ctrl.mappings              = [];
        }

        if (hasMappings) {
            dialogService.showDecisionDialog(
                'Clear All Operations',
                'All selected operations have active field mappings configured. ' +
                'If you proceed, <strong>all operations and their mappings will be permanently removed</strong>. ' +
                'Do you want to continue?'
            ).then(function (decision) {
                if (decision === 'proceed') { _doClearAll(); }
            });
        } else {
            _doClearAll();
        }
    };

    ctrl.toggleCorrelationConfig = function (entry) { entry._configOpen = !entry._configOpen; };

    ctrl.isCorrelationConfigured = function (entry) {
        return !!(entry.correlationKey.sourceType && entry.correlationKey.sourcePath && entry.correlationKey.sourcePath.trim());
    };

    ctrl.allOperationsConfigured = function () {
        if (ctrl.selectedOperations.length === 0) { return false; }
        return ctrl.selectedOperations.every(function (s) { return ctrl.isCorrelationConfigured(s); });
    };

    ctrl.selectInterceptionMode = function (entry, modeId) { entry.interceptionMode = modeId; };

    ctrl.selectCorrelationSource = function (entry, sourceTypeId) {
        if (entry.correlationKey.sourceType === sourceTypeId) { return; }
        entry.correlationKey.sourceType = sourceTypeId;
        entry.correlationKey.sourcePath = '';
    };

    ctrl.getSelectedSourceType = function (entry) {
        if (!entry || !entry.correlationKey.sourceType) { return null; }
        for (var i = 0; i < ctrl.correlationSourceTypes.length; i++) {
            if (ctrl.correlationSourceTypes[i].id === entry.correlationKey.sourceType) {
                return ctrl.correlationSourceTypes[i];
            }
        }
        return null;
    };

    ctrl.getInterceptionModeLabel = function (modeId) {
        var mode = ctrl.interceptionModes.filter(function (m) { return m.id === modeId; })[0];
        return mode ? mode.label : modeId;
    };

    // ----------------------------------------
    // CORRELATION PICKER
    // ----------------------------------------

    ctrl.openCorrelationPicker = function (entry, sourceType) {
        if (!sourceType) { return; }
        ctrl.selectCorrelationSource(entry, sourceType.id);

        if (sourceType.id === 'REQUEST_FIELD' || sourceType.id === 'RESPONSE_FIELD') {
            var jsonSample = sourceType.id === 'REQUEST_FIELD'
                ? _getRequestSample(entry.op)
                : _getOpResponseSample(entry.op);

            if (!jsonSample) {
                $mdToast.show(
                    $mdToast.simple()
                        .textContent('No JSON sample available for this operation.')
                        .position('top right').theme('warn').hideDelay(3000)
                );
                return;
            }

            ctrl.jsonPathSelector = {
                open: true,
                entry: entry,
                sourceType: sourceType,
                attribute: { name: entry.op.operationName || entry.op.summary || entry.op.path },
                jsonSample: jsonSample,
                currentPath: entry.correlationKey.sourcePath || null,
                mappingType: sourceType.id,
                target: 'correlation',
                filter: null
            };
            return;
        }

        var options = entry._optionsByType[sourceType.id] || [];
        if (options.length === 0) { return; }

        $mdDialog.show({
            controller: 'CorrelationKeyPickerController', controllerAs: 'ctrl',
            template: _CORRELATION_PICKER_TEMPLATE, parent: angular.element(document.body),
            clickOutsideToClose: true, escapeToClose: true, multiple: true,
            locals: {
                sourceType: sourceType, options: options, currentValue: entry.correlationKey.sourcePath,
                operationName: entry.op.operationName || entry.op.summary || entry.op.path
            }
        }).then(function (selectedValue) {
            entry.correlationKey.sourcePath = selectedValue;
        }).catch(angular.noop);
    };

    // ----------------------------------------
    // JSON PATH SELECTOR
    // ----------------------------------------

    ctrl.jsonPathSelector = {
        open: false,
        entry: null,
        sourceType: null,
        attribute: null,
        jsonSample: null,
        currentPath: null,
        mappingType: null,
        target: 'correlation',
        filter: null
    };

    ctrl.onCorrelationJsonPathSave = function (selectedPath) {
        if (ctrl.jsonPathSelector.target === 'filter') {
            var f = ctrl.jsonPathSelector.filter;
            if (f) { f.sourcePath = selectedPath; }
        } else {
            var entry = ctrl.jsonPathSelector.entry;
            if (entry) { entry.correlationKey.sourcePath = selectedPath; }
        }
        ctrl.jsonPathSelector.open = false;
    };

    ctrl.onCorrelationJsonPathCancel = function () { ctrl.jsonPathSelector.open = false; };

    // ----------------------------------------
    // FILTER MANAGEMENT
    // ----------------------------------------

    ctrl.addFilter = function (entry) {
        entry.filters.push({
            filterType: null,
            sourcePath: '',
            allowedValues: [],
            caseSensitive: true,
            _newValue: '',
            _open: true
        });
    };

    ctrl.removeFilter = function (entry, idx) { entry.filters.splice(idx, 1); };

    ctrl.toggleFilter = function (filter) {
        filter._open = !filter._open;
    };

    ctrl.toggleAllFilters = function (entry) {
        var anyOpen = entry.filters.some(function (f) { return f._open; });
        entry.filters.forEach(function (f) { f._open = !anyOpen; });
    };

    ctrl.allFiltersOpen = function (entry) {
        return entry.filters.length > 0 && entry.filters.every(function (f) { return f._open; });
    };

    ctrl.selectFilterSourceType = function (entry, filter, sourceTypeObj) {
        if (filter.filterType === sourceTypeObj.id) { return; }
        filter.filterType = sourceTypeObj.id;
        filter.sourcePath = '';
    };

    ctrl.openFilterSourcePicker = function (entry, filter, sourceTypeObj) {
        var prevType = filter.filterType;
        filter.filterType = sourceTypeObj.id;
        if (prevType !== sourceTypeObj.id) { filter.sourcePath = ''; }

        if (sourceTypeObj.id === 'STATUS_CODE') {
            filter.sourcePath = 'status';
            if (prevType !== 'STATUS_CODE') filter.allowedValues = [];
            return;
        }
        if (sourceTypeObj.id === 'CUSTOM_HEADER') { return; }

        if (sourceTypeObj.id === 'REQUEST_FIELD' || sourceTypeObj.id === 'RESPONSE_FIELD') {
            ctrl.openFilterJsonPathSelector(entry, filter);
            return;
        }

        var options = (entry._optionsByType && entry._optionsByType[sourceTypeObj.id]) || [];
        if (options.length === 0) { return; }

        $mdDialog.show({
            controller: 'CorrelationKeyPickerController', controllerAs: 'ctrl',
            template: _CORRELATION_PICKER_TEMPLATE, parent: angular.element(document.body),
            clickOutsideToClose: true, escapeToClose: true, multiple: true,
            locals: {
                sourceType: sourceTypeObj, options: options, currentValue: filter.sourcePath,
                operationName: entry.op.operationName || entry.op.summary || entry.op.path
            }
        }).then(function (selectedValue) {
            filter.sourcePath = selectedValue;
        }).catch(angular.noop);
    };

    ctrl.hasFilterPickerOptions = function (entry, sourceTypeId) {
        if (!entry || !sourceTypeId) { return false; }
        if (sourceTypeId === 'STATUS_CODE' || sourceTypeId === 'CUSTOM_HEADER' || sourceTypeId === 'REQUEST_FIELD' || sourceTypeId === 'RESPONSE_FIELD') { return false; }
        return ((entry._optionsByType && entry._optionsByType[sourceTypeId]) || []).length > 0;
    };

    ctrl.hasFilterOptions = function (entry, sourceTypeId) {
        if (!entry) { return false; }
        if (sourceTypeId === 'STATUS_CODE') { return true; }
        if (sourceTypeId === 'CUSTOM_HEADER') { return true; }
        if (sourceTypeId === 'REQUEST_FIELD') { return !!_getRequestSample(entry.op); }
        if (sourceTypeId === 'RESPONSE_FIELD') { return !!_getOpResponseSample(entry.op); }
        return ((entry._optionsByType && entry._optionsByType[sourceTypeId]) || []).length > 0;
    };

    ctrl.getFilterSourceType = function (filter) {
        if (!filter || !filter.filterType) { return null; }
        return ctrl.filterSourceTypes.filter(function (fst) { return fst.id === filter.filterType; })[0] || null;
    };

    ctrl.addFilterValue = function (filter) {
        var val = (filter._newValue || '').trim();
        if (!val) { return; }
        if (filter.filterType === 'STATUS_CODE' && !/^[1-5][0-9]{2}$/.test(val)) return;
        if (filter.allowedValues.indexOf(val) === -1) { filter.allowedValues.push(val); }
        filter._newValue = '';
    };

    ctrl.statusCodeSuggestions = function (filter) {
        var query = (filter._newValue || '').trim().toLowerCase();
        return ctrl.httpStatusCodes.filter(function (code) {
            var matches = code.indexOf(query) === 0 || ctrl.httpStatusDescription(code).toLowerCase().indexOf(query) !== -1;
            return matches && filter.allowedValues.indexOf(code) === -1;
        });
    };

    ctrl.selectStatusCode = function (filter, code) {
        if (!code) return;
        filter._newValue = code;
        ctrl.addFilterValue(filter);
        filter._selectedStatus = null;
    };

    ctrl.removeFilterValue = function (filter, idx) { filter.allowedValues.splice(idx, 1); };

    var _PILL_COLORS = ['#607d8b', '#8bc34a', '#03a9f4', '#ff9800', '#e91e63',
        '#9c27b0', '#009688', '#cddc39', '#ffc107', '#795548'];

    ctrl.getPillBg = function (val) {
        var s = val || '', hash = 0;
        for (var i = 0; i < s.length; i++) { hash = s.charCodeAt(i) + ((hash << 5) - hash); }
        return _PILL_COLORS[Math.abs(hash) % _PILL_COLORS.length];
    };

    ctrl.getPillColor = function (val) {
        var hex = ctrl.getPillBg(val);
        var r = parseInt(hex.substr(1, 2), 16), g = parseInt(hex.substr(3, 2), 16), b = parseInt(hex.substr(5, 2), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) > 140 ? '#000000' : '#ffffff';
    };

    ctrl.onFilterValueKeydown = function ($event, filter) {
        if ($event.key === ' ' || $event.key === 'Enter') {
            $event.preventDefault();
            ctrl.addFilterValue(filter);
        }
    };

    ctrl.isFilterFreeText = function (filter) {
        return filter.filterType && filter.filterType !== 'STATUS_CODE' &&
            filter.filterType !== 'REQUEST_FIELD' &&
            filter.filterType !== 'RESPONSE_FIELD';
    };

    ctrl.openFilterJsonPathSelector = function (entry, filter) {
        var sampleFn = filter.filterType === 'REQUEST_FIELD'
            ? _getRequestSample : _getOpResponseSample;
        var jsonSample = sampleFn(entry.op);

        if (!jsonSample) {
            $mdToast.show(
                $mdToast.simple()
                    .textContent('No JSON sample available for this operation.')
                    .position('top right').theme('warn').hideDelay(3000)
            );
            return;
        }

        ctrl.jsonPathSelector = {
            open: true,
            entry: entry,
            sourceType: null,
            attribute: { name: entry.op.operationName || entry.op.summary || entry.op.path },
            jsonSample: jsonSample,
            currentPath: filter.sourcePath || null,
            mappingType: filter.filterType,
            target: 'filter',
            filter: filter
        };
    };

    ctrl.hasCorrelationOptions = function (entry, sourceTypeId) {
        if (!entry) { return false; }
        if (sourceTypeId === 'CUSTOM_HEADER') { return true; }
        if (sourceTypeId === 'REQUEST_FIELD') { return !!_getRequestSample(entry.op); }
        if (sourceTypeId === 'RESPONSE_FIELD') { return !!_getOpResponseSample(entry.op); }
        if (!entry._optionsByType) { return false; }
        return (entry._optionsByType[sourceTypeId] || []).length > 0;
    };

    ctrl.getCorrelationOptions = function (entry) {
        if (!entry || !entry.correlationKey || !entry.correlationKey.sourceType) { return []; }
        if (entry._optionsByType) { return entry._optionsByType[entry.correlationKey.sourceType] || []; }
        return _computeCorrelationOptions(entry.op, entry.correlationKey.sourceType);
    };

    // ----------------------------------------
    // CORRELATION PICKER TEMPLATE — redesigned
    // Each option renders structured data:
    //   name       → monospace bold (key/field name)
    //   required   → red badge
    //   type       → purple badge
    //   path       → mono smaller (json path for fields)
    //   description → regular wrapping text
    // ----------------------------------------

    var _CORRELATION_PICKER_TEMPLATE = [
        '<md-dialog class="ckp-dialog" aria-label="Select Correlation Key">',
        '  <md-toolbar class="ckp-toolbar"><div class="md-toolbar-tools">',
        '    <md-icon class="ckp-toolbar-icon">{{ ctrl.sourceType.icon }}</md-icon>',
        '    <div class="ckp-header-info">',
        '      <span class="ckp-title">{{ ctrl.sourceType.label }}</span>',
        '      <span class="ckp-subtitle">{{ ctrl.operationName }}</span>',
        '    </div>',
        '    <span flex></span>',
        '    <md-button class="md-icon-button ckp-close-btn" ng-click="ctrl.cancel()" aria-label="Close">',
        '      <md-icon>close</md-icon>',
        '    </md-button>',
        '  </div></md-toolbar>',

        '  <md-dialog-content class="ckp-content">',

        '    <div class="ckp-search-wrap">',
        '      <md-icon class="ckp-search-icon">search</md-icon>',
        '      <input type="text" class="ckp-search-input" ng-model="ctrl.search"',
        '             placeholder="Search…" autocomplete="off" />',
        '      <md-icon class="ckp-clear-icon" ng-if="ctrl.search"',
        '               ng-click="ctrl.search = \'\'">close</md-icon>',
        '    </div>',

        '    <div class="ckp-options">',

        '      <div ng-repeat="opt in ctrl.filteredOptions() track by opt.value"',
        '           class="ckp-option"',
        '           ng-class="{\'ckp-option--selected\': ctrl.currentValue === opt.value}"',
        '           ng-click="ctrl.select(opt)">',

        '        <div class="ckp-option-radio">',
        '          <div class="ckp-option-radio-dot"></div>',
        '        </div>',

        '        <div class="ckp-option-body">',

        '          <div class="ckp-option-name-row">',
        '            <span class="ckp-option-name">{{ opt.name }}</span>',
        '            <span class="ckp-option-required" ng-if="opt.required">required</span>',
        '            <span class="ckp-option-type" ng-if="opt.type">{{ opt.type }}</span>',
        '          </div>',

        '          <span class="ckp-option-path"',
        '                ng-if="opt.path && opt.path !== opt.name">{{ opt.path }}</span>',

        '          <span class="ckp-option-desc" ng-if="opt.description">{{ opt.description }}</span>',

        '        </div>',
        '      </div>',

        '      <div class="ckp-empty" ng-if="ctrl.filteredOptions().length === 0">',
        '        No results for "{{ ctrl.search }}"',
        '      </div>',

        '    </div>',
        '  </md-dialog-content>',

        '  <md-dialog-actions class="ckp-footer">',
        '    <span flex></span>',
        '    <md-button class="cbew-btn cbew-btn--cancel" ng-click="ctrl.cancel()">Cancel</md-button>',
        '  </md-dialog-actions>',
        '</md-dialog>'
    ].join('\n');

    // ----------------------------------------
    // CORRELATION OPTIONS — structured objects
    // Returns { value, name, description, type, path, required }
    // ----------------------------------------

    function _computeCorrelationOptions(op, sourceType) {
        if (!op || !sourceType) { return []; }

        switch (sourceType) {

            case 'HEADER':
                return (op.httpHeaders || []).map(function (p) {
                    return {
                        value: p.name,
                        name: p.name,
                        description: p.description || null,
                        type: p.type || null,
                        path: null,
                        required: !!p.required
                    };
                });

            case 'URI_PARAM':
                return (op.uriParams || []).map(function (p) {
                    return {
                        value: p.name,
                        name: p.name,
                        description: p.description || null,
                        type: p.type || 'string',
                        path: null,
                        required: !!p.required
                    };
                });

            case 'QUERY_PARAM':
                return (op.queryParams || []).map(function (p) {
                    return {
                        value: p.name,
                        name: p.name,
                        description: p.description || null,
                        type: p.type || 'string',
                        path: null,
                        required: !!p.required
                    };
                });

            case 'REQUEST_FIELD': return _payloadFieldOptions(op, 'request');
            case 'RESPONSE_FIELD': return _payloadFieldOptions(op, 'response');
            default: return [];
        }
    }

    function _payloadFieldOptions(op, side) {
        var payload = op.payload;
        if (!payload) { return []; }

        var body = null;
        if (side === 'request') {
            body = payload.request;
        } else {
            var responses = payload.responses || {};
            body = responses['200'] || responses['201'] || responses['202'] || null;
            if (!body || !body.fields || !body.fields.length) {
                var keys = Object.keys(responses);
                for (var i = 0; i < keys.length; i++) {
                    var c = responses[keys[i]];
                    if (c && c.fields && c.fields.length > 0) { body = c; break; }
                }
            }
        }

        if (!body || !body.fields || !body.fields.length) { return []; }

        return body.fields
            .filter(function (f) {
                if (!f.path || f.path === '$') { return false; }
                var t = (f.type || 'string').toLowerCase();
                return t !== 'object' && t !== 'array';
            })
            .map(function (f) {
                return {
                    value: f.path,
                    name: f.name || f.path,
                    description: f.description || null,
                    type: f.format ? (f.type + '/' + f.format) : (f.type || 'string'),
                    path: f.path,
                    required: !!f.required
                };
            });
    }

    function _getRequestSample(op) {
        return (op.payload && op.payload.request && op.payload.request.json_sample)
            ? op.payload.request.json_sample : null;
    }

    function _getOpResponseSample(op) {
        if (!op.payload || !op.payload.responses) { return null; }
        var responses = op.payload.responses;
        var preferred = ['200', '201', '202'];
        for (var i = 0; i < preferred.length; i++) {
            if (responses[preferred[i]] && responses[preferred[i]].json_sample) { return responses[preferred[i]].json_sample; }
        }
        var keys = Object.keys(responses);
        for (var j = 0; j < keys.length; j++) {
            if (keys[j].charAt(0) === '2' && responses[keys[j]].json_sample) { return responses[keys[j]].json_sample; }
        }
        return null;
    }

    // ----------------------------------------
    // OPERATION DETAIL POPUP
    // ----------------------------------------

    ctrl.openOperationDetail = function (op, $event) {
        if ($event) { $event.stopPropagation(); }
        var swagger = ctrl.browsingCollection && ctrl.browsingCollection.swagger;
        if (!swagger) { return; }
        $mdDialog.show({
            controller: 'BizEventOperationDetailController', controllerAs: 'ctrl',
            templateUrl: 'profile/tabs/biz-events/create-wizard/operation-detail-dialog.html',
            parent: angular.element(document.body), clickOutsideToClose: true, escapeToClose: true, multiple: true,
            locals: { operation: op, filteredSpec: _buildSingleOpSpec(swagger, op._pathKey, op._method) }
        }).catch(angular.noop);
    };

    // ----------------------------------------
    // JSON SAMPLE VIEWER
    // ----------------------------------------

    ctrl.jsonViewer = { open: false, json: null, title: null };

    ctrl.hasRequestSample = function (op) { return !!(op.payload && op.payload.request && op.payload.request.json_sample); };
    ctrl.hasResponseSample = function (op) { return !!ctrl._getResponseSample(op); };

    ctrl._getResponseSample = function (op) {
        if (!op.payload || !op.payload.responses) { return null; }
        var responses = op.payload.responses;
        var preferred = ['200', '201', '202'];
        for (var i = 0; i < preferred.length; i++) {
            var r = responses[preferred[i]];
            if (r && r.json_sample) { return r.json_sample; }
        }
        var keys = Object.keys(responses);
        for (var j = 0; j < keys.length; j++) {
            if (keys[j].startsWith('2') && responses[keys[j]].json_sample) { return responses[keys[j]].json_sample; }
        }
        return null;
    };

    ctrl.openJsonSampleViewer = function (op, side, $event) {
        if ($event) { $event.stopPropagation(); }
        var json = side === 'request' ? op.payload.request.json_sample : ctrl._getResponseSample(op);
        if (!json) { return; }
        ctrl.jsonViewer = {
            open: true, json: json,
            title: (side === 'request' ? 'Request' : 'Response') + ' — ' + (op.operationName || op.summary || op.path)
        };
    };

    ctrl.closeJsonViewer = function () { ctrl.jsonViewer.open = false; };

    function _buildSingleOpSpec(swagger, pathKey, method) {
        var filtered = angular.copy(swagger);
        var singlePath = {};
        singlePath[pathKey] = {};
        singlePath[pathKey][method] = swagger.paths[pathKey][method];
        if (swagger.paths[pathKey].parameters) { singlePath[pathKey].parameters = swagger.paths[pathKey].parameters; }
        filtered.paths = singlePath;
        return filtered;
    }

    // ========================================
    // STEP 2 — SELECT TARGET
    // ========================================

    ctrl.publishedTemplates = [];
    ctrl.loadingTemplates = false;
    ctrl.selectedTemplate = null;
    ctrl.selectedTemplateFull = null;
    ctrl.frameTypes = [];
    ctrl.loadingFrameTypes = false;
    ctrl.selectedFrameType = null;

    ctrl.shortId = function (id, n) { return id ? id.slice(-(n || 10)) : ''; };

    ctrl.loadPublishedTemplates = function () {
        ctrl.loadingTemplates = true;
        ctrl.publishedTemplates = []; ctrl.selectedTemplate = null; ctrl.selectedTemplateFull = null;
        ctrl.frameTypes = []; ctrl.selectedFrameType = null;

        // Only template versions with API collections: a biz event can use no
        // other APIs than its template's.
        ProfileService.getTemplateTree(profile.id, null, { withApiCollections: true })
            .then(function (response) {
                var data = response && response.data ? response.data : null;
                var root = data;
                if (root && root['template-tree']) { root = root['template-tree']; }
                if (root && root.templateTree) { root = root.templateTree; }
                if (!root || !root.id) { ctrl.publishedTemplates = []; return; }

                var flat = [];
                function walk(node) {
                    if (!node || !node.id) { return; }
                    var status = node.status || node.templateStatus;
                    var version = node.version || node.templateVersion;
                    if (_isSelectableStatus(status) && !node.filtered_out) {
                        flat.push({
                            id: node.id, version: version, status: status,
                            name: node.name || node.templateName || node.templateDesc || null
                        });
                    }
                    (node.children || []).forEach(function (child) { walk(child); });
                }
                walk(root);
                ctrl.publishedTemplates = flat;
            })
            .catch(function (err) {
                console.error('[BizEventWizard] Error loading template tree:', err);
                dialogService.showErrorDialog('Error Loading Templates', (err.data && err.data.message) || 'Could not load templates.');
            })
            .finally(function () { ctrl.loadingTemplates = false; });
    };

    ctrl.selectTemplate = function (node) {
        if (ctrl.selectedTemplate && ctrl.selectedTemplate.id === node.id) { return; }
        ctrl.selectedTemplate = node; ctrl.selectedTemplateFull = null;
        ctrl.selectedFrameType = null; ctrl.frameTypes = []; ctrl.loadingFrameTypes = true;

        TemplateInstanceService.getTemplateInstanceById(node.id)
            .then(function (response) {
                var full = response.data || response;
                ctrl.selectedTemplateFull = full;
                ctrl.frameTypes = (full.templateConfig && full.templateConfig.frame_types) || [];
            })
            .catch(function (err) { console.error('[BizEventWizard] Error loading template:', err); ctrl.frameTypes = []; })
            .finally(function () { ctrl.loadingFrameTypes = false; });
    };

    ctrl.selectFrameType = function (ft) { ctrl.selectedFrameType = ft; };

    // Version of the template the frame types come from — shown as a pill next to the
    // frame type everywhere in the wizard (list, IntelliSense bar/results, composer).
    ctrl.templateVersion = function () {
        var t = ctrl.selectedTemplate, f = ctrl.selectedTemplateFull;
        return (t && (t.version || t.templateVersion)) || (f && (f.templateVersion || f.version)) || null;
    };
    ctrl.frameTypeName = function (ft) { return ft.frame_name || ft.name || ft.frame_type_name || '(unnamed)'; };
    ctrl.frameTypeDesc = function (ft) { return ft.frame_description || ft.description || ft.frame_type_description || ''; };
    ctrl.frameTypeComponentCount = function (ft) { return (ft.frame_fields || ft.components || ft.frame_components || []).length; };

    // ========================================
    // WIZARD NAVIGATION
    // ========================================

    ctrl.canAdvance = function () {
        switch (ctrl.currentStep) {
            case 1: return !!ctrl.selectedSourceType;
            case 2: return !!ctrl.selectedTemplate && !!ctrl.selectedFrameType;
            case 3: return ctrl.selectedOperations.length > 0;
            case 4: return ctrl.allOperationsConfigured();
            case 5: return !!(ctrl.bizEventName && ctrl.bizEventName.trim()) && !ctrl.nameAlreadyExists && !ctrl.loadingNames;
            case 6: return ctrl.allRequiredComponentsMapped() && !ctrl.bmelErrors;
            default: return true;
        }
    };

    // Returns true when every frame component flagged as required
    // has at least one mapping entry pointing to it.
    ctrl.allRequiredComponentsMapped = function () {
        if (!ctrl.selectedFrameType) { return false; }
        var comps = ctrl.selectedFrameType.frame_fields
                 || ctrl.selectedFrameType.components
                 || ctrl.selectedFrameType.frame_components
                 || [];
        var required = comps.filter(function (c) { return c.is_required; });
        if (required.length === 0) { return true; }
        return required.every(function (c) {
            var compId = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
            return (ctrl.mappings || []).some(function (m) {
                return m.targetComponentId === compId;
            });
        });
    };

    // Returns how many required components are still unmapped (for UI hints).
    ctrl.unmappedRequiredCount = function () {
        if (!ctrl.selectedFrameType) { return 0; }
        var comps = ctrl.selectedFrameType.frame_fields
                 || ctrl.selectedFrameType.components
                 || ctrl.selectedFrameType.frame_components
                 || [];
        return comps.filter(function (c) {
            if (!c.is_required) { return false; }
            var compId = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
            return !(ctrl.mappings || []).some(function (m) { return m.targetComponentId === compId; });
        }).length;
    };

    ctrl.nextStep = function () {
        if (!ctrl.canAdvance()) { return; }
        // Leaving Configure Operations: operations and cardinalities are final,
        // so this is the single point where the agent builds the mappings.
        if (ctrl.currentStep === 4 && _agentMappingNeeded()) {
            _confirmRemap()
                .then(function (remap) { return remap ? _runAgentMapping() : null; })
                .finally(_advanceStep);
            return;
        }
        _advanceStep();
    };

    function _advanceStep() {
        if (ctrl.currentStep === 1 && ctrl.publishedTemplates.length === 0)  { ctrl.loadPublishedTemplates(); }
        if (ctrl.currentStep === 3) { ctrl._autoDetectCorrelation(); }
        if (ctrl.currentStep === 4) { ctrl._generateEventIdentity(); }
        if (ctrl.currentStep === 5) { ctrl._initMappingStep(); }
        if (ctrl.currentStep < ctrl.totalSteps) { ctrl.currentStep++; } else { ctrl.submit(); }
    }

    // What the agent's mappings depend on: the target frame and each selected
    // operation with its cardinality (order-independent).
    function _agentSignature() {
        return JSON.stringify({
            frameTypeId: ctrl.selectedFrameType ? ctrl.selectedFrameType.frame_type_id : null,
            operations:  ctrl.selectedOperations.map(function (s) {
                return String(s.op.operationId || s.op.operation_id || s.op.id) + ':' + (s.cardinality || 'ONE');
            }).sort()
        });
    }

    // Operations were picked with IntelliSense and the source/target differs
    // from what the agent last mapped — first pass, or the user went back and
    // changed operations, collections, cardinality or the target frame.
    function _agentMappingNeeded() {
        return ctrl._intelliSenseUsed
            && ctrl.selectedOperations.length > 0
            && _agentSignature() !== ctrl._agentInputs;
    }

    // Re-mapping replaces what the composer holds: ask first when it isn't empty.
    // Declining keeps the current mappings and doesn't ask again for this
    // same configuration.
    function _confirmRemap() {
        if (!(ctrl.mappings && ctrl.mappings.length)) { return $q.when(true); }
        return dialogService.showDecisionDialog(
            'Override existing mappings?',
            'The operations or the target frame changed since IntelliSense mapped them.<br><br>' +
            '<strong>Yes</strong> maps them again and overrides the existing mappings.<br>' +
            '<strong>No</strong> keeps the existing configuration.',
            { goBack: 'No', proceed: 'Yes' }
        ).then(function (decision) {
            if (decision === 'proceed') { return true; }
            ctrl._agentInputs = _agentSignature();
            return false;
        });
    }

    // Agent pass: maps exactly the selected operations with their cardinality
    // and replaces the composer's mappings with the result (the composer applies
    // the preload onto an empty mapping). On cancel / failure the current
    // mappings stay, and we don't retry for this configuration on every Next —
    // IntelliSense can still be run from the mapping step.
    function _runAgentMapping() {
        var signature = _agentSignature();
        var ops = ctrl.selectedOperations.map(function (s) {
            return _toApiOperation(s.op, s.cardinality);
        });
        return _runIntelliSenseJob(ops, {
            engine:       BusinessEventsService.INTELLISENSE_ENGINE.AGENT,
            dialogTitle:  'Building Mappings...',
            initialStage: 'The IntelliSense agent is mapping your operations…'
        })
            .then(function (res) {
                ctrl.mappings              = [];
                ctrl.targetExpressions     = {};
                ctrl.bmelErrors            = false;
                ctrl._intelliSenseMappings = res.mappings;
                ctrl._agentInputs          = signature;
            })
            .catch(function (err) {
                ctrl._agentInputs = signature;
                if (err && !err.cancelled) {
                    dialogService.showErrorDialog(err.title || 'IntelliSense Failed',
                        (err.message || 'IntelliSense processing failed.') + ' You can run IntelliSense again from the mapping step.');
                }
            });
    }

    ctrl.prevStep = function () { if (ctrl.currentStep > 1) { ctrl.currentStep--; } };

    // ── Auto-correlation detection ────────────────────────────────────────────
    // Called when advancing from step 4 (Select Operations) → step 5 (Configure).
    // Only pre-fills operations that have not yet been manually configured.
    // Priority: (1) common HTTP Header, (2) common URI Parameter.
    // "Common" means the field name appears in EVERY selected operation's options
    // for that source type.
    ctrl._autoDetectCorrelation = function () {
        var ops = ctrl.selectedOperations;
        if (!ops || ops.length === 0) { return; }

        // Find the best common field across all operations for a given sourceType.
        // Returns the field name string, or null if none found.
        function _findCommonField(sourceTypeId) {
            // Collect the set of field names available in op[0]
            var candidates = (ops[0]._optionsByType[sourceTypeId] || []).map(function (o) { return o.value; });
            if (candidates.length === 0) { return null; }

            // Keep only names present in every other operation
            for (var i = 1; i < ops.length; i++) {
                var available = {};
                (ops[i]._optionsByType[sourceTypeId] || []).forEach(function (o) { available[o.value] = true; });
                candidates = candidates.filter(function (c) { return available[c]; });
                if (candidates.length === 0) { return null; }
            }

            // Among survivors, prefer required fields first, then alphabetical
            var required = candidates.filter(function (name) {
                var opt = (ops[0]._optionsByType[sourceTypeId] || []).filter(function (o) { return o.value === name; })[0];
                return opt && opt.required;
            });
            return required.length > 0 ? required[0] : candidates[0];
        }

        // Determine the best suggestion: HEADER first, then URI_PARAM
        var bestType = null;
        var bestField = null;

        var headerField = _findCommonField('HEADER');
        if (headerField) {
            bestType  = 'HEADER';
            bestField = headerField;
        } else {
            var uriField = _findCommonField('URI_PARAM');
            if (uriField) {
                bestType  = 'URI_PARAM';
                bestField = uriField;
            }
        }

        if (!bestType) { return; }  // no common field found — leave everything untouched

        // Pre-fill only operations whose correlation key hasn't been set yet
        ops.forEach(function (entry) {
            if (entry.correlationKey.sourceType || entry.correlationKey.sourcePath) { return; }
            entry.correlationKey.sourceType = bestType;
            entry.correlationKey.sourcePath = bestField;
        });
    };

    // ========================================
    // SUBMIT
    // ========================================

    ctrl.submit = function () {
        ctrl.submitting = true;
        BusinessEventsService.create(_buildCreatePayload())
            .then(function () { $mdDialog.hide({ created: true }); })
            .catch(function (err) {
                console.error('[BizEventWizard] Error creating biz event:', err);
                dialogService.showErrorDialog(
                    'Error Creating Business Event',
                    (err.data && err.data.message) || 'Could not create the business event.'
                );
            })
            .finally(function () { ctrl.submitting = false; });
    };

    // ── Builds the full CreateBizEventDTO payload ─────────────────────────────
    function _buildCreatePayload() {
        return {
            biz_event_name:        ctrl.bizEventName.trim(),
            biz_event_description: ctrl.bizEventDescription || '',
            account_id:            profile.accountId || profile.account_id,
            profile_id:            profile.id,
            biz_event_trigger: {
                biz_event_trigger_id:          ctrl.selectedSourceType.id,
                biz_event_trigger_description: ''
            },
            biz_event_sources: ctrl.selectedOperations.map(function (s) {
                return {
                    source_type:             ctrl.selectedSourceType.id,
                    event_interception_mode: s.interceptionMode,
                    cardinality:             s.cardinality || 'ONE',
                    api_collection_id:       s.collection.collection_id,
                    api_collection_name:     s.collection.collection_name,
                    api_collection_version:  s.collection.api_version || null,
                    operation_id:            s.op.operationId,
                    operation_name:          s.op.operationName || s.op.summary || s.op.path,
                    api_method:              s.op.method,
                    api_endpoint:            s.op.path,
                    api_signature:           s.op.signature || s.op.path,
                    correlation_key: {
                        source_type: s.correlationKey.sourceType,
                        source_path: s.correlationKey.sourcePath
                    },
                    filters: (s.filters || [])
                        .filter(function (f) {
                            return f.filterType && f.sourcePath && f.sourcePath.trim() && f.allowedValues.length > 0;
                        })
                        .map(function (f) {
                            return {
                                filter_type:    f.filterType,
                                source_path:    f.sourcePath.trim(),
                                allowed_values: f.allowedValues,
                                case_sensitive: f.caseSensitive
                            };
                        })
                };
            }),
            biz_event_target: {
                template_instance_id:      ctrl.selectedTemplate.id,
                template_instance_version: ctrl.selectedTemplate.version,
                frame_type_id:             ctrl.selectedFrameType.frame_type_id,
                frame_type_name:           ctrl.frameTypeName(ctrl.selectedFrameType)
            },
            biz_event_mappings: _buildMappingsPayload()
        };
    }

    // ── Converts ctrl.mappings → List<BizEventMapping> ───────────────────────
    //
    // BizEventMapping:
    //   sources   → [{ operation_name, source_type, source_path, placeholder }]
    //   target    → { component_id, component_name, component_type,
    //                 attribute_id, attribute_name }
    //   transform → { expression }   (BMEL from ctrl.targetExpressions)
    //
    // NESTED_TABLE adds one extra entry per column (attribute_name = colName).
    // ─────────────────────────────────────────────────────────────────────────
    function _buildMappingsPayload() {
        var result     = [];
        var frameComps = ctrl.selectedFrameType.frame_fields
                      || ctrl.selectedFrameType.components
                      || ctrl.selectedFrameType.frame_components
                      || [];

        function _findFrameComp(compId) {
            return frameComps.filter(function (c) {
                var cid = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
                return String(cid) === String(compId);
            })[0] || null;
        }

        function _makeSource(src, placeholder) {
            var opEntry = ctrl.selectedOperations[src.opIdx];
            if (!opEntry) { return null; }

            // CONTEXT sources must be split into REQUEST_CONTEXT / RESPONSE_CONTEXT
            // so the backend can persist and re-serve them with semantic precision.
            // The directive stores all context vars under sourceType='CONTEXT' and
            // distinguishes req vs res by path prefix ($bm.response.* → response).
            var backendSourceType = src.sourceType;
            if (src.sourceType === 'CONTEXT') {
                var p = src.path || '';
                backendSourceType = (p.indexOf('$bm.response') === 0)
                    ? 'RESPONSE_CONTEXT'
                    : 'REQUEST_CONTEXT';
            }

            return {
                operation_name: opEntry.op.operationName || opEntry.op.summary || opEntry.op.path,
                source_type:    backendSourceType,
                source_path:    src.path,
                placeholder:    placeholder || null
            };
        }

        ctrl.mappings.forEach(function (m) {
            var mappingSource = _makeSource(m.source, null);
            if (!mappingSource) { return; }   // operation was removed — skip

            var fc       = _findFrameComp(m.targetComponentId);
            var compType = (fc && (fc.frame_component_type || fc.component_type)) || 'FIELD_REFERENCE';
            var compId   = fc ? (fc.frame_component_id || fc.id || fc.component_id || null) : null;
            var attrId   = fc ? (fc.attribute_type_id   || null) : null;
            var attrName = fc ? (fc.attribute_type_name || null) : null;
            var expr     = ctrl.targetExpressions[m.targetComponentId] || m.bmelExpression || null;

            // ── Main component entry ────────────────────────────────────────
            result.push({
                sources:   [mappingSource],
                target: {
                    component_id:   compId,
                    component_name: m.targetComponentName,
                    component_type: compType,
                    attribute_id:   attrId,
                    attribute_name: attrName
                },
                transform: expr ? { expression: expr } : null
            });

            // ── Column entries for NESTED_TABLE ─────────────────────────────
            if (m.columnMappings) {
                Object.keys(m.columnMappings).forEach(function (colName) {
                    var cm = m.columnMappings[colName];
                    if (!cm || !cm.source) { return; }
                    var colSource = _makeSource(cm.source, null);
                    if (!colSource) { return; }
                    var colExpr = ctrl.targetExpressions[m.targetComponentId + '|col|' + colName]
                               || cm.bmelExpression || null;
                    result.push({
                        sources:   [colSource],
                        target: {
                            component_id:   compId,
                            component_name: m.targetComponentName,
                            component_type: 'NESTED_TABLE',
                            attribute_id:   attrId,
                            attribute_name: colName
                        },
                        transform: colExpr ? { expression: colExpr } : null
                    });
                });
            }
        });

        return result;
    }

    ctrl.cancel = function () {
        dialogService.showDecisionDialog(
            'Cancel Business Event',
            'Are you sure you want to cancel? All the information you have entered will be discarded.'
        ).then(function (decision) {
            if (decision === 'proceed') {
                $mdDialog.cancel();
            }
        });
    };

    // ========================================
    // STEP 6 — FIELD MAPPING
    // ========================================

    ctrl.mappings          = [];
    ctrl.targetExpressions = {};
    ctrl.bmelErrors        = false;


    // ========================================
    // INTELLISENSE OPERATION SELECTION
    // ========================================

    // Normalizes a wizard operation object into the shape the IntelliSense
    // API expects. Shared by the discovery pass and by the agent pass
    // below, so both build the request payload identically.
    function _toApiOperation(op, cardinality) {
        return {
            operation_id:   op.operationId   || op.operation_id   || op.id,
            operation_name: op.operationName || op.operation_name || op.summary || op.path,
            method:         op.method,
            path:           op.path,
            summary:        op.summary || null,
            payload:        op.payload || null,
            cardinality:    cardinality || 'ONE'
        };
    }

    // Runs one IntelliSense mapping job end-to-end: starts it, shows a
    // progress dialog, chain-polls until it settles, and resolves with
    // { mappings, summary }. Rejects with { cancelled: true } if the user
    // cancels the dialog, or with { title, message } on any other failure.
    //
    // opts (optional):
    //   engine        — BusinessEventsService.INTELLISENSE_ENGINE.* (default
    //                   DETERMINISTIC); also sets the polling interval.
    //   dialogTitle   — overrides the progress dialog's toolbar title.
    //   initialStage  — overrides the stage text shown before the first
    //                   progress event arrives.
    //
    // Used twice: by ctrl.runIntelliSenseSelection with the deterministic
    // engine against every scoped operation (so the results dialog can show
    // the user which operations best match the frame type), and by
    // _runAgentMapping with the agent, when leaving Configure Operations, to
    // build the mappings handed to the mapping step (~30-60 s).
    function _runIntelliSenseJob(operations, opts) {
        opts = opts || {};
        var engine = opts.engine || BusinessEventsService.INTELLISENSE_ENGINE.DETERMINISTIC;
        // $q, not native Promise: everything chained onto this — including
        // ctrl.selectedOperations / ctrl._intelliSenseMappings assignments in
        // runIntelliSenseSelection's .then() handlers below — needs to run
        // inside Angular's digest cycle so the view actually re-renders when
        // it resolves. A native Promise here would still resolve correctly,
        // but its .then() callbacks fire as plain JS microtasks outside any
        // $digest, so the state change wouldn't reach the DOM until some
        // unrelated event happened to trigger the next digest — looking like
        // a multi-second freeze with no network activity to explain it.
        return $q(function (resolve, reject) {
            var _cancelled   = false;
            var _jobId       = null;   // known once /start answers — needed to cancel the agent
            var _pollTimeout = null;   // $timeout handle for chained polling

            function _stopPoll() {
                if (_pollTimeout) { $timeout.cancel(_pollTimeout); _pollTimeout = null; }
            }

            // ── Show in-progress dialog ────────────────────────────────────
            // Always shown — both the operation discovery and the agent pass
            // hit the backend; the agent takes ~30-60 s.
            $mdDialog.show({
                multiple: true,
                clickOutsideToClose: false,
                escapeToClose: false,
                template: _IS_PROGRESS_DIALOG_TEMPLATE,
                controller: function ($scope, $mdDialog) {
                    $scope.percent     = 0;
                    $scope.stage       = opts.initialStage || 'Starting…';
                    $scope.dialogTitle = opts.dialogTitle   || null;   // falls back to default in the template

                    // Listen to progress broadcasts emitted by the SSE handler
                    $scope.$on('intellisense:progress', function (e, data) {
                        $scope.percent = data.percent || 0;
                        $scope.stage   = data.message || '';
                    });

                    $scope.cancelIntelliSense = function () {
                        _cancelled = true;
                        _stopPoll();
                        BusinessEventsService.cancelIntelliSense(_jobId, engine);   // stop the agent server-side
                        $mdDialog.hide('cancelled');
                        reject({ cancelled: true });
                    };
                }
            });

            // Chained $timeout polling — only ONE request in flight at a time.
            // This prevents the "job not found" race where $interval fires a second
            // request before the first complete response is processed.
            function _schedulePoll(jobId) {
                if (_cancelled) { return; }
                _pollTimeout = $timeout(function () {

                    BusinessEventsService.pollIntelliSenseStatus(jobId, engine)
                        .then(function (res) {
                            if (_cancelled) { return; }
                            var data = res.data;

                            $rootScope.$broadcast('intellisense:progress', {
                                percent: data.percent || 0,
                                message: data.message  || ''
                            });

                            if (data.status === 'COMPLETE') {
                                _stopPoll();

                                // Broadcast 100% and let the bar animate to the end
                                // before closing (CSS transition is 400ms).
                                $rootScope.$broadcast('intellisense:progress', {
                                    percent: 100,
                                    message: 'Done'
                                });

                                $timeout(function () {
                                    $mdDialog.hide();
                                    resolve({
                                        mappings: (data.result && data.result.mappings) || [],
                                        summary:  (data.result && data.result.summary)  || {}
                                    });
                                }, 400);  // let the 100% bar render before closing

                            } else if (data.status === 'ERROR') {
                                _stopPoll();
                                $mdDialog.hide();   // closes only the progress dialog
                                reject({
                                    title:   'IntelliSense Failed',
                                    message: data.message ||
                                        'IntelliSense processing failed. Please try again or map the fields manually.'
                                });

                            } else {
                                // Still running — schedule the next poll only after
                                // this response is fully processed.
                                _schedulePoll(jobId);
                            }
                        })
                        .catch(function (err) {
                            if (_cancelled) { return; }
                            _stopPoll();
                            $mdDialog.hide();   // closes only the progress dialog
                            reject({
                                title:   'IntelliSense Connection Error',
                                message: (err.data && (err.data.detail || err.data.message)) ||
                                    'Lost connection to IntelliSense service. Please try again.'
                            });
                        });

                }, BusinessEventsService.intelliSensePollInterval(engine));
            }

            // ── Step 1: POST to start the async job ────────────────────────
            BusinessEventsService.startIntelliSense({
                operations:    operations,
                template:      ctrl.selectedTemplateFull,
                frame_type_id: ctrl.selectedFrameType.frame_type_id
            }, engine)
            .then(function (response) {
                _jobId = response.data && response.data.job_id;
                if (_cancelled) { BusinessEventsService.cancelIntelliSense(_jobId, engine); return; }

                var jobId = response.data && response.data.job_id;
                if (!jobId) { throw new Error('No job_id returned by server'); }

                // ── Step 2: start chained poll ────────────────────────────
                _schedulePoll(jobId);
            })
            .catch(function (err) {
                if (_cancelled) { return; }
                console.error('[BizEventWizard] IntelliSense start error:', err);
                $mdDialog.hide();   // closes only the progress dialog
                _stopPoll();
                reject({
                    title:   'IntelliSense Failed',
                    message: (err.data && (err.data.detail || err.data.message)) ||
                        'Failed to start IntelliSense. Please try again.'
                });
            });
        });
    }

    ctrl.runIntelliSenseSelection = function () {
        if (ctrl.intelliSenseSelecting) { return; }
        if (!ctrl.selectedTemplateFull || !ctrl.selectedFrameType) { return; }
        if (ctrl.scopedCollections.length === 0) {
            ctrl.intelliSenseSelectionError = 'Browse at least one API collection first.';
            return;
        }

        ctrl.intelliSenseSelecting      = true;
        ctrl.intelliSenseSelectionError = null;

        // Populated by pass 1, read back by the results dialog below
        var discoveredOps = [];

        // ── Pass 1 (discover, deterministic engine): run against EVERY scoped
        // operation so the results dialog can show the user which operations
        // best match the frame type, regardless of which ones they keep.
        // Its mappings are only used to pick operations — the agent builds
        // the real ones when leaving Configure Operations.
        var allOps = [];
        ctrl.scopedCollections.forEach(function (sc) {
            (sc.operations || []).forEach(function (op) {
                allOps.push(_toApiOperation(op));
            });
        });

        _runIntelliSenseJob(allOps, { engine: BusinessEventsService.INTELLISENSE_ENGINE.DETERMINISTIC })
            .then(function (discovery) {
                var mappings = discovery.mappings;

                var matchedIds = {};
                mappings.forEach(function (m) {
                    (m.sources || []).forEach(function (src) {
                        if (src.operation_id) { matchedIds[src.operation_id] = true; }
                    });
                });

                discoveredOps = [];
                ctrl.scopedCollections.forEach(function (sc) {
                    (sc.operations || []).forEach(function (op) {
                        var opId = op.operationId || op.operation_id || op.id;
                        if (matchedIds[String(opId)]) {
                            discoveredOps.push({ op: op, collection: sc.collection });
                        }
                    });
                });

                return $mdDialog.show({
                    controller:          'IntelliSenseResultsController',
                    controllerAs:        'ctrl',
                    template:            _IS_RESULTS_DIALOG_TEMPLATE,
                    parent:              angular.element(document.body),
                    clickOutsideToClose: false,
                    escapeToClose:       true,
                    multiple:            true,
                    locals: {
                        discoveredOps: discoveredOps,
                        mappings:      mappings,
                        summary:       discovery.summary,
                        frameTypeName: ctrl.selectedFrameType ? ctrl.frameTypeName(ctrl.selectedFrameType) : '',
                        templateVersion: ctrl.templateVersion()
                    }
                });
            })
            .then(function (result) {
                // result is either { action: 'proceed', selectedOps } or the
                // plain string 'cancel' (from the results dialog's Cancel/X).
                if (!result || result.action !== 'proceed') {
                    ctrl.intelliSenseSelecting = false;
                    return;
                }

                ctrl.selectedOperations = [];
                result.selectedOps.forEach(function (d) {
                    ctrl._addOperation(d.op, d.collection);
                });

                // The agent builds the mappings once, when leaving Configure
                // Operations — after the user sets each operation's cardinality.
                ctrl._intelliSenseMappings      = null;
                ctrl._intelliSenseUsed          = result.selectedOps.length > 0;
                ctrl.intelliSenseSelectionError = null;
                ctrl.intelliSenseSelecting      = false;
            })
            .catch(function (err) {
                ctrl.intelliSenseSelecting = false;
                if (!err || err.cancelled) { return; }   // user cancelled — nothing to report
                // Don't keep stale/mismatched mappings around after a failed pass.
                ctrl._intelliSenseMappings = null;
                dialogService.showErrorDialog(
                    err.title   || 'IntelliSense Failed',
                    err.message || 'IntelliSense processing failed. Please try again or map the fields manually.'
                );
            });
    };

    ctrl._initMappingStep = function () {
        if (!ctrl.mappings || !Array.isArray(ctrl.mappings)) { ctrl.mappings = []; }

        // Pre-load Monaco + BMEL function registry so the tokenizer has
        // KNOWN_FN_NAMES populated before the first editor opens.
        // Without this, Monaco registers the BMEL language with an empty
        // function list → functions get red underlines on first open.
        Promise.all([
            BmelEditorService.loadMonaco(),
            BmelEditorService.getFunctions().length
                ? Promise.resolve()
                : BmelEditorService.loadFunctions()
        ]).then(function () {
            BmelEditorService.refreshLanguageTokenizer();
        }).catch(angular.noop);
    };

    // ========================================
    // INTELLISENSE DIALOG TEMPLATES
    // ========================================

    // (c) In-progress dialog — determinate progress bar, real-time stage message
    var _IS_PROGRESS_DIALOG_TEMPLATE = [
        '<md-dialog class="cbew-is-progress-dialog" aria-label="IntelliSense in progress">',

        '  <md-toolbar style="background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);',
        '               min-height:64px; flex-shrink:0;">',
        '    <div class="md-toolbar-tools">',
        '      <md-icon style="color:white; margin-right:10px; flex-shrink:0;">auto_awesome</md-icon>',
        '      <h2 style="color:white; font-weight:500; font-size:18px; margin:0;',
        '                 flex:1; text-align:left;">{{ dialogTitle || "IntelliSense in Progress..." }}</h2>',
        '    </div>',
        '  </md-toolbar>',

        '  <md-dialog-content style="padding:24px 28px 20px; background:#fff; min-width:420px;">',

        '    <!-- Stage message with continuous spinner -->',
        '    <div style="display:flex; align-items:center; gap:12px; margin-bottom:16px;">',
        '      <md-progress-circular md-mode="indeterminate" md-diameter="22"',
        '          style="flex-shrink:0; --md-progress-color:#764ba2;"></md-progress-circular>',
        '      <span style="font-size:13px; color:#546e7a; flex:1;">{{ stage || "Starting..." }}</span>',
        '      <span style="font-size:13px; font-weight:700; color:#764ba2;',
        '                   min-width:36px; text-align:right;">{{ percent }}%</span>',
        '    </div>',

        '    <!-- Determinate progress bar -->',
        '    <div style="height:8px; background:#ede7f6; border-radius:4px; overflow:hidden;">',
        '      <div style="height:100%; border-radius:4px;',
        '                  background:linear-gradient(90deg,#667eea 0%,#764ba2 100%);',
        '                  transition:width 0.4s ease;"',
        '           ng-style="{width: (percent || 0) + \'%\'}">',
        '      </div>',
        '    </div>',

        '    <p style="margin:10px 0 0 0; font-size:12px; color:#b0bec5; text-align:right;">',
        '      You can cancel at any time.',
        '    </p>',
        '  </md-dialog-content>',

        '  <!-- Native button — avoids md-button event binding issues in compiled string templates -->',
        '  <div style="display:flex; justify-content:flex-end; padding:10px 16px;',
        '              background:#fafafa; border-top:1px solid #eeeeee;">',
        '    <button type="button" ng-click="cancelIntelliSense()"',
        '            style="background:#f5f5f5; color:#666; border:1px solid #ddd;',
        '                   border-radius:4px; font-size:13px; font-weight:500;',
        '                   height:34px; padding:0 20px; cursor:pointer; font-family:inherit;">',
        '      Cancel',
        '    </button>',
        '  </div>',

        '</md-dialog>'
    ].join('\n');

    // (d) Results dialog
    var _IS_RESULTS_DIALOG_TEMPLATE = [
        '<md-dialog class="cbew-is-results-dialog" aria-label="IntelliSense Results" style="max-width:92vw;">',
        '  <md-toolbar style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height:64px; flex-shrink:0;">',
        '    <div class="md-toolbar-tools">',
        '      <md-icon style="color:white; margin-right:10px;">auto_awesome</md-icon>',
        '      <div style="flex:1; display:flex; flex-direction:column; gap:1px; text-align:left;">',
        '        <h2 style="color:white; font-weight:500; font-size:18px; margin:0; text-align:left;">IntelliSense Results</h2>',
        '        <span style="color:rgba(255,255,255,0.8); font-size:12px; text-align:left;">',
        '          {{ ctrl.discoveredOps.length > 0 ? (ctrl.selectedCount() + \' of \' + ctrl.discoveredOps.length + \' operation\' + (ctrl.discoveredOps.length === 1 ? \'\' : \'s\') + \' selected\') : \'No matches found\' }}',
        '        </span>',
        '      </div>',
        '      <md-button class="md-icon-button" ng-click="ctrl.dismiss()" style="color:white; margin:0;">',
        '        <md-icon>close</md-icon>',
        '      </md-button>',
        '    </div>',
        '  </md-toolbar>',

        '  <md-dialog-content style="padding:0; background:#fff; width:860px; max-width:92vw;">',

        '    <!-- NO RESULTS state -->',
        '    <div ng-if="ctrl.discoveredOps.length === 0"',
        '         style="padding:36px 28px; display:flex; flex-direction:column; align-items:center; gap:16px; text-align:center;">',
        '      <div style="width:56px; height:56px; border-radius:50%; background:#f3f0ff;',
        '                  display:flex; align-items:center; justify-content:center; flex-shrink:0;">',
        '        <md-icon style="font-size:28px; width:28px; height:28px; color:#9575cd;">search_off</md-icon>',
        '      </div>',
        '      <div>',
        '        <p style="margin:0 0 8px 0; font-size:14px; font-weight:600; color:#37474f;">No matches found</p>',
        '        <p style="margin:0; font-size:13px; color:#78909c; line-height:1.6; max-width:340px;">',
        '          IntelliSense detected no API matches for the current API collection selection.',
        '        </p>',
        '      </div>',
        '    </div>',

        '    <!-- HAS RESULTS state -->',
        '    <div ng-if="ctrl.discoveredOps.length > 0">',
        '      <!-- Summary banner -->',
        '      <div style="padding:14px 24px; background:#f3f0ff; border-bottom:1px solid #e0e0e0;',
        '                  display:flex; align-items:center; gap:10px;">',
        '        <md-icon style="color:#764ba2; font-size:20px; width:20px; height:20px; flex-shrink:0;">info_outline</md-icon>',
        '        <span style="font-size:13px; color:#4a3f6b; line-height:1.4; flex:1;">',
        '          The following operations were matched for frame type <strong>{{ ctrl.frameTypeName }}</strong><span class="cbew-tpl-version-pill cbew-op-version-pill" ng-if="ctrl.templateVersion">v{{ ctrl.templateVersion }}</span>.',
        '          Check the ones you want to import, then click <strong>Proceed</strong>.',
        '        </span>',
        '        <label style="display:flex; align-items:center; gap:6px; cursor:pointer; flex-shrink:0;',
        '                      font-size:12px; color:#4a3f6b; user-select:none; white-space:nowrap;">',
        '          <input type="checkbox"',
        '                 ng-checked="ctrl.allSelected()"',
        '                 ng-click="ctrl.toggleAll()"',
        '                 style="width:15px; height:15px; accent-color:#764ba2; cursor:pointer; flex-shrink:0;">',
        '          Select all',
        '        </label>',
        '      </div>',
        '      <!-- Operations list -->',
        '      <div style="max-height:60vh; overflow-y:auto;">',
        '        <div ng-repeat="item in ctrl.discoveredOps track by $index"',
        '             style="display:flex; align-items:center; gap:14px; padding:12px 24px;',
        '                    border-bottom:1px solid #f0f0f0; cursor:pointer;"',
        '             ng-class="{\'cbew-is-result-row--alt\': $index % 2 === 1, \'cbew-is-result-row--selected\': ctrl.selected[$index]}"',
        '             ng-click="ctrl.selected[$index] = !ctrl.selected[$index]">',
        '          <input type="checkbox"',
        '                 ng-model="ctrl.selected[$index]"',
        '                 ng-click="$event.stopPropagation()"',
        '                 style="width:15px; height:15px; accent-color:#764ba2; cursor:pointer; flex-shrink:0;">',
        '          <span class="cbew-method-badge cbew-method-badge--{{ item.op.method | lowercase }}">{{ item.op.method }}</span>',
        '          <div style="flex:1; min-width:0;">',
        '            <div style="font-size:13px; font-weight:500; color:#37474f; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">',
        '              {{ item.op.operationName || item.op.summary || item.op.path }}',
        '              <span class="cbew-tpl-version-pill cbew-op-version-pill" ng-if="item.collection.api_version">v{{ item.collection.api_version }}</span>',
        '            </div>',
        '            <div style="font-size:11px; color:#90a4ae; font-family:monospace; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">',
        '              {{ item.op.path }}',
        '            </div>',
        '          </div>',
        '          <span style="font-size:11px; color:#9575cd; background:#f3f0ff; padding:3px 10px;',
        '                       border-radius:10px; white-space:nowrap; flex-shrink:0; max-width:45%;',
        '                       overflow:hidden; text-overflow:ellipsis;"',
        '                title="{{ item.collection.collection_name }} v{{ item.collection.api_version }}">',
        '            {{ item.collection.collection_name }}',
        '          </span>',
        '        </div>',
        '      </div>',
        '    </div>',
        '  </md-dialog-content>',

        '  <md-dialog-actions style="padding:12px 16px; background:#fafafa; border-top:1px solid #e0e0e0;',
        '                            display:flex; justify-content:flex-end; gap:8px; min-height:52px;">',
        '    <!-- Cancel only shown when there ARE results -->',
        '    <md-button ng-if="ctrl.discoveredOps.length > 0" class="cbew-btn cbew-btn--cancel" ng-click="ctrl.cancel()">Cancel</md-button>',
        '    <md-button class="md-raised cbew-is-proceed-btn" ng-click="ctrl.proceed()"',
        '               ng-disabled="ctrl.discoveredOps.length > 0 && ctrl.selectedCount() === 0">',
        '      <md-icon style="font-size:18px; width:18px; height:18px; margin-right:4px;">check_circle</md-icon>',
        '      Proceed{{ ctrl.discoveredOps.length > 0 && ctrl.selectedCount() > 0 ? \' (\' + ctrl.selectedCount() + \')\' : \'\' }}',
        '    </md-button>',
        '  </md-dialog-actions>',
        '</md-dialog>'
    ].join('\n');

});


// ================================================================
//  BizEventOperationDetailController
// ================================================================

angular.module('bizmetryApp').controller('BizEventOperationDetailController', function (
    $scope, $mdDialog, $timeout, operation, filteredSpec
) {
    const ctrl = this;
    $scope.ctrl = ctrl;
    ctrl.operation = operation;
    ctrl.loading = true;

    // operationId is a generated identifier (method + path hash) — the title
    // is the operation's own name.
    ctrl.title = function () {
        return operation.operationName || operation.summary || operation.path;
    };

    // Only the operation is shown: drop the collection-level info (the API's
    // long markdown description) so Swagger UI doesn't render it at all.
    ctrl.filteredSpec = angular.copy(filteredSpec);
    if (ctrl.filteredSpec && ctrl.filteredSpec.info) {
        ctrl.filteredSpec.info = { title: '', version: ctrl.filteredSpec.info.version || '' };
    }

    function loadSwaggerUI() {
        if (window.SwaggerUIBundle) { renderSwagger(); return; }
        var cssId = 'swagger-ui-css';
        if (!document.getElementById(cssId)) {
            var link = document.createElement('link');
            link.id = cssId; link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/swagger-ui-dist@5/swagger-ui.css';
            document.head.appendChild(link);
        }
        var script = document.createElement('script');
        script.src = 'https://unpkg.com/swagger-ui-dist@5/swagger-ui-bundle.js';
        script.onload = renderSwagger;
        document.head.appendChild(script);
    }

    function renderSwagger() {
        ctrl.loading = false;
        $timeout(function () {
            window.SwaggerUIBundle({
                spec: ctrl.filteredSpec, domNode: document.getElementById('cbew-swagger-op-container'),
                presets: [window.SwaggerUIBundle.presets.apis, window.SwaggerUIBundle.SwaggerUIStandalonePreset],
                layout: 'BaseLayout', deepLinking: false, displayRequestDuration: true,
                defaultModelsExpandDepth: -1, tryItOutEnabled: false, docExpansion: 'full'
            });
        }, 0);
    }

    $timeout(loadSwaggerUI, 150);
    ctrl.close = function () { $mdDialog.hide(); };
});


// ================================================================
//  CorrelationKeyPickerController — redesigned
//  filteredOptions() searches across name + description + path
// ================================================================

angular.module('bizmetryApp').controller('CorrelationKeyPickerController', function (
    $scope, $mdDialog, sourceType, options, currentValue, operationName
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    ctrl.sourceType = sourceType;
    ctrl.options = options;
    ctrl.currentValue = currentValue;
    ctrl.operationName = operationName;
    ctrl.search = '';

    ctrl.filteredOptions = function () {
        if (!ctrl.search || !ctrl.search.trim()) { return ctrl.options; }
        var term = ctrl.search.trim().toLowerCase();
        return ctrl.options.filter(function (opt) {
            return (opt.name || '').toLowerCase().indexOf(term) !== -1 ||
                (opt.description || '').toLowerCase().indexOf(term) !== -1 ||
                (opt.path || '').toLowerCase().indexOf(term) !== -1;
        });
    };

    ctrl.select = function (opt) { $mdDialog.hide(opt.value); };
    ctrl.cancel = function () { $mdDialog.cancel(); };
});

// ================================================================
//  IntelliSenseResultsController
//  Muestra las operaciones descubiertas; el usuario elige Proceed o Cancel
// ================================================================

angular.module('bizmetryApp').controller('IntelliSenseResultsController', function (
    $scope, $mdDialog, discoveredOps, mappings, summary, frameTypeName, templateVersion
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    ctrl.discoveredOps = discoveredOps;
    ctrl.mappings      = mappings;
    ctrl.summary       = summary;
    ctrl.frameTypeName = (summary && summary.frame_type_name) || frameTypeName || '';
    ctrl.templateVersion = templateVersion || null;

    // Selection state — all checked by default
    ctrl.selected = {};
    discoveredOps.forEach(function (_, i) { ctrl.selected[i] = true; });

    ctrl.selectedCount = function () {
        return discoveredOps.filter(function (_, i) { return !!ctrl.selected[i]; }).length;
    };

    ctrl.allSelected = function () {
        return discoveredOps.length > 0 && ctrl.selectedCount() === discoveredOps.length;
    };

    ctrl.toggleAll = function () {
        var shouldSelectAll = !ctrl.allSelected();
        discoveredOps.forEach(function (_, i) { ctrl.selected[i] = shouldSelectAll; });
    };

    // Proceed: returns only the checked operations to the caller
    ctrl.proceed = function () {
        if (discoveredOps.length === 0) { $mdDialog.hide('cancel'); return; }
        var chosen = discoveredOps.filter(function (_, i) { return !!ctrl.selected[i]; });
        if (chosen.length === 0) { $mdDialog.hide('cancel'); return; }
        $mdDialog.hide({ action: 'proceed', selectedOps: chosen });
    };
    // Cancel: discard results, keep current selection
    ctrl.cancel  = function () { $mdDialog.hide('cancel'); };
    // Dismiss via X button (always treated as cancel)
    ctrl.dismiss = function () { $mdDialog.hide('cancel'); };
});